import * as React from 'react'
import { PeriodToggle } from './PlanCards'
import { formatMoney } from './useChargebeeCheckout'

// Left column of /upgrade: pick the plan, see what it costs. Every amount
// below the plan list comes from the server's estimate — the plan row prices
// are the published list prices from the variant config.
function PlanRow({ option, selected, isCurrent, disabled, onSelect }) {
    return (
        <button
            type="button"
            onClick={() => onSelect(option.key)}
            aria-pressed={selected}
            disabled={disabled}
            data-testid={`plan-row-${option.key}`}
            className={`tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-bg-card tw-p-4 tw-text-left tw-text-foreground disabled:tw-cursor-default disabled:tw-opacity-60 ${
                selected
                    ? 'tw-border-primary tw-shadow-sm'
                    : 'tw-border-border hover:tw-border-muted-foreground'
            }`}
        >
            <span
                aria-hidden="true"
                className={`tw-mt-1 tw-flex tw-h-4 tw-w-4 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid ${
                    selected ? 'tw-border-primary' : 'tw-border-border'
                }`}
            >
                {selected ? (
                    <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary" />
                ) : null}
            </span>
            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                <span className="tw-flex tw-items-center tw-gap-2 tw-font-semibold">
                    {option.label}
                    {isCurrent ? (
                        <span
                            className="tw-rounded tw-bg-muted tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground"
                            data-testid="plan-card-current"
                        >
                            Current
                        </span>
                    ) : null}
                </span>
                {option.highlights?.anchor ? (
                    <span className="tw-text-xs tw-font-medium tw-text-muted-foreground">
                        {option.highlights.anchor}
                    </span>
                ) : null}
                {option.highlights?.hero ? (
                    <span className="tw-text-sm tw-font-semibold tw-text-foreground">
                        {option.highlights.hero}
                    </span>
                ) : null}
                <ul className="tw-m-0 tw-list-disc tw-pl-4 tw-text-sm tw-text-muted-foreground">
                    {(option.highlights?.features || []).map(feature => (
                        <li key={feature}>{feature}</li>
                    ))}
                </ul>
            </span>
            <span className="tw-flex tw-flex-none tw-flex-col tw-items-end">
                <span className="tw-font-semibold">
                    {option.currency
                        ? formatMoney(option.amount * 100, option.currency)
                        : `€${option.amount}`}
                </span>
                <span className="tw-text-xs tw-text-muted-foreground">
                    /mo
                </span>
            </span>
        </button>
    )
}

function Line({ label, value, testId, strong = false, muted = false }) {
    return (
        <div
            className={`tw-flex tw-items-baseline tw-justify-between tw-gap-4 ${
                strong ? 'tw-font-semibold' : ''
            }`}
        >
            <span
                className={muted ? 'tw-text-muted-foreground' : undefined}
                data-testid={testId}
            >
                {label}
            </span>
            <span
                className="tw-tabular-nums"
                data-testid={testId ? `${testId}-value` : undefined}
            >
                {value}
            </span>
        </div>
    )
}

export default function CheckoutSummary({
    options,
    selectedKey,
    onSelectPlan,
    period,
    onPeriodChange,
    saving,
    currentPlanId,
    quote,
    loading,
    mode,
    country,
    onCompare,
}) {
    const currency = quote && quote.currency
    const money = amount => formatMoney(amount, currency)
    const selected = options.find(option => option.key === selectedKey)

    return (
        <section
            className="tw-flex tw-flex-col tw-gap-5"
            data-testid="checkout-summary"
        >
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <PeriodToggle
                    period={period}
                    onChange={onPeriodChange}
                    saving={saving}
                />
                <button
                    type="button"
                    onClick={onCompare}
                    className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary tw-underline-offset-4 hover:tw-underline"
                >
                    Compare all features
                </button>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-2">
                {options.map(option => (
                    <PlanRow
                        key={option.key}
                        option={option}
                        selected={option.key === selectedKey}
                        isCurrent={currentPlanId === option.planId}
                        disabled={
                            mode === 'change' &&
                            currentPlanId === option.planId
                        }
                        onSelect={onSelectPlan}
                    />
                ))}
            </div>

            {mode === 'change' ? null : (
                <div
                    className={`tw-flex tw-flex-col tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-border tw-pt-4 tw-text-sm ${
                        loading ? 'tw-opacity-60' : ''
                    }`}
                    aria-busy={loading || undefined}
                >
                    <Line
                        label={`${selected?.label || 'Plan'} · ${
                            period === 'annual' ? 'billed yearly' : 'billed monthly'
                        }`}
                        value={quote ? money(quote.subTotal) : '—'}
                        muted
                    />
                    {(quote?.discounts || []).map(discount => (
                        <Line
                            key={discount.couponId || discount.description}
                            label={`${discount.description || 'Discount'}${
                                discount.couponId ? ` (${discount.couponId})` : ''
                            }`}
                            value={`−${money(discount.amount)}`}
                            testId="payment-discount"
                            muted
                        />
                    ))}
                    {country ? null : (
                        <Line
                            label="Tax"
                            value="Choose your country"
                            testId="payment-tax-pending"
                            muted
                        />
                    )}
                    {(quote?.taxes || []).map(tax => (
                        <Line
                            key={tax.name || tax.description}
                            label={`${tax.name || tax.description || 'Tax'}${
                                tax.rate != null ? ` (${Number(tax.rate)}%)` : ''
                            }`}
                            value={money(tax.amount)}
                            muted
                        />
                    ))}
                    <Line
                        label={country ? 'Total due today' : 'Total before tax'}
                        value={quote ? money(quote.amount) : 'calculating…'}
                        testId="payment-total"
                        strong
                    />
                </div>
            )}

        </section>
    )
}
